import { useState } from "react"
import { Flame } from "lucide-react"
import { ForgeButton } from "@/components/ui/forge-button"

type Props = {
  // Judge's one-paragraph rationale stamped on TestRun.summary by RunOneWorker.
  // We frame it as "Forge thinks…" so the failure surface feels collaborative,
  // not just a robot verdict. Forge picks up the conversation in Design.
  judgeSummary: string | null
  onFix: () => Promise<void> | void
}

// Yellow callout that surfaces above the failed transcript. Click "Fix with
// Forge" → fetches the synthesized operator message (`discuss_payload`) and
// switches to the Design tab with the composer pre-filled — the existing
// flow that ships today, just relabeled and made the primary failure CTA.
export function ForgeOnFailure({ judgeSummary, onFix }: Props) {
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    setBusy(true)
    try {
      await onFix()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-card border border-status-warning/40 bg-status-warning/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-app-text">
        <Flame size={16} className="text-status-warning" />
        Forge thinks
      </div>
      <p className="mb-3 whitespace-pre-wrap text-sm text-app-muted">
        {judgeSummary?.trim()
          ? judgeSummary
          : "The run failed without a summary. Open the conversation to see what happened."}
      </p>
      <ForgeButton
        size="sm"
        onClick={handleClick}
        disabled={busy}
      >
        {busy ? "Loading…" : "Fix with Forge"}
      </ForgeButton>
    </div>
  )
}
